import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
  sendEmailVerification,
  signOut,
} from "firebase/auth";
import dotPattern from "../../assets/Dot Pattern.svg";
import { FunctionComponent, useState } from "react";
import { auth } from "../../utilities/firebase";
import { GoogleSignInIcon } from "../../ui/icons/GoogleSignInIcon";
import { Formik, Form } from "formik";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { registrationScheme } from "../../utilities/validationSchema";
import { useUpdateUserData } from "../../features/ProfilePage/api/client";
import FormInput from "../../ui/components/FormInput/FormInput";
import Illustration from "../../features/LoginPage/components/Illustration";
import VerificationDrawer, {
  RESEND_COOLDOWN_SECONDS,
  VERIFY_EMAIL_STORAGE_KEY,
} from "../../features/SignUpPage/components/VerificationDrawer";
import Seo from "../../ui/components/Seo/Seo";
import { analyticsEvents } from "../../utilities/analyticsEvents";
import useResendVerification from "../../hooks/useResendVerification";
import { getSafeRedirect } from "../../services/api/Routing/routingUtils";
import { startCooldown } from "../../hooks/useCooldown";
import {showErrorToast} from "../../utilities/toasts";
interface LoginPageProps {}

const SignUpPage: FunctionComponent<LoginPageProps> = () => {
  const [isGoogleLoading, setIsGoogleLoading] = useState<boolean>(false);
  const { mutateAsync: updateUserAsync } = useUpdateUserData();

  const { isOpen: isDrawerOpen, onOpen, onClose } = useDisclosure();
  const { setCredentials, handleResend } = useResendVerification();

  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerClose = () => {
    onClose();
    navigate("/login", { replace: true, state: location.state });
  };

  const signUpWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    setIsGoogleLoading(true);
    try {
      const user = await signInWithPopup(auth, provider);
      try {
        await updateUserAsync({
          userId: user.user.uid,
          userData: {
            name: user.user?.displayName?.split(" ")[0] ?? "",
            surname:
              user.user?.displayName?.split(" ").slice(1).join(" ") ?? "",
            email: user.user.email ?? "",
          },
        });
      } catch {}
      analyticsEvents.googleSignUp();
      const target = getSafeRedirect(location.state?.from);
      navigate(target, { replace: true });
    } catch (err: any) {
      showErrorToast("Щось пішло не так, спробуйте ще раз.");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const signUp = async (
    email: string,
    password: string,
    name: string,
    surname: string
  ) => {
    let userCreated = false;
    try {
      setCredentials(email, password);

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      userCreated = true;

      await sendEmailVerification(userCredential.user);

      await updateProfile(userCredential.user, {
        displayName: `${name} ${surname}`,
      });

      try {
        await updateUserAsync({
          userId: userCredential.user.uid,
          userData: {
            name: name,
            surname: surname,
            email: userCredential.user.email ?? "",
          },
        });
      } catch {}

      analyticsEvents.passwordSignUp();

      await signOut(auth);

      startCooldown(VERIFY_EMAIL_STORAGE_KEY, RESEND_COOLDOWN_SECONDS);

      onOpen();
    } catch (error: any) {
      if (userCreated) {
        await signOut(auth);
      }
      const message =
        error.code === "auth/email-already-in-use"
          ? "Акаунт з даною поштою вже існує."
          : "Щось пішло не так, спробуйте ще раз.";
      showErrorToast(message);
    }
  };

  return (
    <>
      <Seo description={`Реєстрація в Читанці`} title={"Реєстрація"} />
      <Flex
        px={{ base: "0.5rem", sm: "0rem" }}
        paddingTop={{ base: "1.25rem", sm: "0rem" }}
        flexDirection={{ base: "column", lg: "row" }}
        rowGap="1rem"
        alignItems="center"
        gap={{ base: "1.5rem", lg: "7.7%" }}
      >
        <Illustration marginTop={{ base: "2.5rem", lg: "0rem" }} />
        <Box
          order={{ base: 1, lg: 2 }}
          paddingRight={{
            base: "0rem",
            sm: "1rem",
            md: "2rem",
            xl: "3rem",
            "2xl": "4.75rem",
          }}
          flexGrow={1}
        >
          <Text
            fontWeight={600}
            as="h1"
            fontSize={{
              base: "1.625rem",
              sm: "1.5rem",
              md: "1,75rem",
              xl: "2.25rem",
            }}
            line-height="2.75rem"
            marginBottom={{ base: "0.5rem", xl: "0.75rem" }}
          >
            Реєстрація
          </Text>
          <HStack
            marginBottom={{
              base: "1rem",
              sm: "1.5rem",
              md: "1.75rem",
              xl: "2rem",
              "2xl": "2.5rem",
            }}
          >
            <Text
              size={{ base: "caption", sm: "xs", md: "sm", xl: "md" }}
              variant="secondary"
            >
              Вже зареєстровані?
            </Text>
            <NavLink to="/login" replace state={location.state}>
              <Text
                size={{ base: "caption", sm: "xs", md: "sm", xl: "md" }}
                fontWeight={600}
                textDecoration="underline"
                variant="link"
                as="h2"
              >
                Увійти
              </Text>
            </NavLink>
          </HStack>
          <HStack
            justifyContent="center"
            alignItems="center"
            minWidth={{ bsae: "auto", lg: "48%" }}
            transition="all 0.3s"
            _hover={{ backgroundColor: "black", color: "white" }}
            color="black"
            onClick={isGoogleLoading ? undefined : signUpWithGoogle}
            opacity={isGoogleLoading ? 0.7 : 1}
            cursor={isGoogleLoading ? "not-allowed" : "pointer"}
            marginBottom={{
              base: "1.5rem",
              sm: "0.625rem",
              md: "1.25rem",
              lg: "1.875rem",
              xl: "3.125rem",
            }}
            spacing={{ base: "0.75rem", md: "1rem", xl: "1.125rem" }}
            borderRadius="0.5rem"
            width={{ base: "100%", sm: "fit-content" }}
            backgroundColor="grey"
            py={{ base: "1rem", sm: "0.5rem", md: "0.75rem", xl: "1.125rem" }}
            px={{
              base: "0.5rem",
              sm: "2rem",
              md: "2.75rem",
              xl: "3.625rem",
            }}
          >
            {isGoogleLoading ? (
              <Spinner size="sm" color="orange" />
            ) : (
              <GoogleSignInIcon height="1.5rem" width="1.5rem" />
            )}
            <Text
              color="inherit"
              textAlign={{ base: "center", sm: "left" }}
              size={{ base: "md", sm: "xs", md: "sm", xl: "md" }}
              fontWeight={500}
            >
              Зареєструватись через Google
            </Text>
          </HStack>
          <HStack
            width="100%"
            spacing={{
              base: "1rem",
              sm: "0.75rem",
              xl: "1.25rem",
              "2xl": "1.5rem",
            }}
          >
            <Image
              alt="Pattern"
              objectFit="cover"
              backgroundRepeat="repeat"
              width="100%"
              height="0.065rem"
              overflow="hidden"
              src={dotPattern}
            />
            <Text
              variant="secondary"
              size={{ base: "md", sm: "xs", md: "sm", xl: "md" }}
            >
              або
            </Text>
            <Image
              alt="Pattern"
              objectFit="cover"
              height="0.065rem"
              width="100%"
              overflow="hidden"
              src={dotPattern}
            />
          </HStack>
          <Formik
            validationSchema={registrationScheme}
            validateOnBlur={false}
            validateOnChange={false}
            initialValues={{ name: "", surname: "", email: "", password: "" }}
            onSubmit={async (values, actions) => {
              try {
                await signUp(
                  values.email,
                  values.password,
                  values.name,
                  values.surname
                );
              } finally {
                actions.setSubmitting(false);
              }
            }}
          >
            {(props) => (
              <Form>
                <Stack
                  marginTop={{
                    base: "1.5rem",
                    sm: "0.75rem",
                    md: "1.25rem",
                    lg: "1.875rem",
                    xl: "3.125rem",
                  }}
                  spacing={{
                    base: "1.25rem",
                    sm: "1rem",
                    md: "1.25rem",
                    lg: "1.5rem",
                    xl: "2rem",
                  }}
                >
                  <HStack
                    flexDirection={{ base: "column", sm: "row" }}
                    alignItems="flex-start"
                    rowGap="1.25rem"
                    spacing={{
                      base: "0rem",
                      sm: "1rem",
                      xl: "1.5rem",
                      "2xl": "2rem",
                    }}
                  >
                    <FormInput
                      name="name"
                      label="Ім'я"
                      type="name"
                      placeholder="Введіть ваше ім'я"
                    />
                    <FormInput
                      name="surname"
                      label="Прізвище"
                      type="name"
                      placeholder="Введіть ваше прізвище"
                    />
                  </HStack>
                  <FormInput
                    name="email"
                    label="Email"
                    type="email"
                    placeholder="Введіть email адресу"
                  />
                  <FormInput
                    name="password"
                    label="Пароль"
                    type="password"
                    placeholder="Придумайте пароль"
                  />

                  <Button
                    margin="0"
                    isLoading={props.isSubmitting}
                    isDisabled={props.isSubmitting || isGoogleLoading}
                    minWidth={{ base: "100%", sm: "auto", lg: "48%" }}
                    type="submit"
                    height="fit-content"
                    transition="all 0.3s"
                    _active={{}}
                    _hover={{ backgroundColor: "black" }}
                    cursor="pointer"
                    borderRadius={{
                      base: "0.5rem",
                      sm: "0.35rem",
                      xl: "0.5rem",
                    }}
                    backgroundColor="orange"
                    width="fit-content"
                    px={{
                      base: "3rem",
                      sm: "6rem",
                      xl: "7.5rem",
                      "2xl": "8.125rem",
                    }}
                    py={{
                      base: "1.125rem",
                      sm: "0.75rem",
                      md: "1rem",
                      xl: "1.125rem",
                    }}
                  >
                    <Text
                      line-height="1.25rem"
                      fontWeight={600}
                      color="white"
                      size={{ base: "md", sm: "xs", md: "sm", xl: "md" }}
                    >
                      Зареєструватись
                    </Text>
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
          <ToastContainer />
          <VerificationDrawer
            isOpen={isDrawerOpen}
            onClose={handleDrawerClose}
            onResend={handleResend}
          />
        </Box>
      </Flex>
    </>
  );
};

export default SignUpPage;
